<script lang="ts" setup>
/*
  Design specs for Headings (font-size / line-height) regarding Figma

  Heading XXXL (h1)
    mobile:  3rem (48px) / 3.375rem (54px)
    tablet:  4.5rem (72px) / 5.063rem (81px)
    desktop: 6rem (96px) / 6.75rem (108px)

  Heading XXL (h2)
    mobile:  2rem (32px) / 2.5rem (40px)
    tablet:  2.5rem (40px) / 3rem (48px)
    desktop: 3.5rem (56px) / 4rem (64px)

  Heading XL (h3)
    mobile:  2rem (32px) / 2.5rem (40px)
    tablet:  2rem (32px) / 2.5rem (40px)
    desktop: 2.5rem (40px) / 3rem (48px)

  Heading L (h4)
    mobile:  1.5rem (24px) / 1.75rem (28px)
    tablet:  1.5rem (24px) / 1.75rem (28px)
    desktop: 2rem (32px) / 2.25rem (36px)

  Heading M (h5)
    mobile:  1.125rem (18px) / 1.5rem (24px)
    tablet:  1.5rem (18px) / 1.75rem (28px)
    desktop: 1.5rem (24px) / 1.75rem (28px)

  Heading S (h6)
    mobile:  0.875rem (14px) / 1.25rem (20px)
    tablet:  1.125rem (18px) / 1.75rem (28px)
    desktop: 1.125rem (18px) / 1.75rem (28px)
*/
import { computed } from "vue";

export interface HeadingProps {
  /** The text color of the heading. */
  color?: keyof typeof headingClassName.color;
  /** The font size of the heading. */
  size?: keyof typeof headingClassName.size;
  /** The HTML tag to be used. */
  tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div" | "span"; // keyof HTMLElementTagNameMap;
  /** The font weight of the heading. */
  weight?: keyof typeof headingClassName.weight;
}

const {
  color = "dark",
  size = "large",
  tag: customTag,
  weight = "bold",
} = defineProps<HeadingProps>();

const headingClassName = {
  color: {
    light: "text-white",
    dark: "text-black",
    blue: "text-dark-blue",
  },
  size: {
    xxxlarge: "text-5xl md:text-7xl xl:text-8xl",
    xxlarge: "text-3xl md:text-4xl xl:text-6xl",
    xlarge: "text-3xl xl:text-4xl",
    large: "text-2xl xl:text-3xl",
    medium: "text-lg md:text-2xl",
    small: "text-base md:text-lg", // changed text-sm to text-base in difference to design (design is wrong IMHO - smallest heading should not be smaller than body text)
  },
  weight: {
    normal: "font-normal",
    bold: "font-medium",
  },
};

// HTML tag matched by size, if 'tag' is omitted.
const headingTag = {
  xxxlarge: "h1",
  xxlarge: "h2",
  xlarge: "h3",
  large: "h4",
  medium: "h5",
  small: "h6",
};

const computedTag = computed(() => customTag || headingTag[size] || "h4");
</script>

<template>
  <component
    :is="computedTag"
    class="hyphens-auto"
    :class="[
      headingClassName.color[color],
      headingClassName.size[size],
      headingClassName.weight[weight],
    ]"
    data-testid="clr-heading"
  >
    <slot />
  </component>
</template>
